import React from "react";
import { X, ExternalLink } from "lucide-react";
import Button from "../ui/Button";

type AddTeamMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dspName: string;
};

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  isOpen,
  onClose,
  dspName,
}) => {
  if (!isOpen) return null;

  // Dynamic content based on DSP
  const getModalContent = () => {
    switch (dspName) {
      case "Spotify for Artists":
        return {
          title: "Add Team Member to Spotify for Artists",
          instructions: (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-black">Spotify Instructions:</p>
              <ol className="space-y-3 text-sm text-neutral-700 list-decimal list-inside">
                <li>
                  Visit the "manage teams" page on Spotify{" "}
                  <a
                    href="https://manage.spotify.com/teams"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-700 hover:text-primary-700 hover:underline"
                  >
                    https://manage.spotify.com/teams
                    <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>
                  Select the team for the artist you'd like to add me to, or your larger label
                  account team if all artists are in one account
                </li>
                <li>Click "Invite" and enter team member's email and title (optional)</li>
                <li className="ml-6">
                  <strong>Email:</strong> sam@cadenceio.com
                </li>
                <li>Check "Set team member as team admin" - required to submit pitches</li>
                <li>Click "Send Invite"</li>
                <li>
                  I'll receive an email notification and accept the invitation within 24 hours.
                </li>
              </ol>
              <div className="p-3 mt-4 border-l-4 border-primary-500 bg-primary-50">
                <p className="text-sm text-neutral-700">
                  <strong>Additional Resource:</strong>{" "}
                  <a
                    href="https://support.spotify.com/us/artists/article/inviting-team-members-to-spotify-for-artists/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-700 hover:text-primary-700 hover:underline"
                  >
                    Spotify Help - Inviting Team Members
                    <ExternalLink size={14} className="inline ml-1" />
                  </a>
                </p>
              </div>
            </div>
          ),
        };

      case "Amazon Music for Artists":
        return {
          title: "Add Team Member to Amazon Music for Artists",
          instructions: (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-black">Amazon Instructions:</p>
              <div className="p-3 mb-4 border-l-4 border-warning-500 bg-warning-50">
                <p className="text-sm font-medium text-black">
                  <strong>Note:</strong> This process must be completed for all artists you'd
                  like to have Cadence pitch for
                </p>
              </div>
              <ol className="space-y-3 text-sm text-neutral-700 list-decimal list-inside">
                <li>Navigate to "Settings" in the upper right corner of the artist page</li>
                <li>
                  Select "Manage Team Members"{" "}
                  <a
                    href="https://artists.amazon.com/team/current"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-700 hover:text-primary-700 hover:underline"
                  >
                    https://artists.amazon.com/team/current
                    <ExternalLink size={14} className="ml-1" />
                  </a>
                </li>
                <li>Click "Add Team Member"</li>
                <li>
                  Enter team member's information:
                  <ul className="mt-2 ml-6 space-y-1 list-disc list-inside">
                    <li>
                      <strong>Name:</strong> Sam Friske
                    </li>
                    <li>
                      <strong>Email:</strong> sam@cadenceio.com
                    </li>
                    <li>
                      <strong>Access Type:</strong> Admin - this will allow me to submit pitches
                    </li>
                    <li>
                      <strong>Company:</strong> Cadence IO
                    </li>
                    <li>
                      <strong>Role:</strong> Label or Distributor
                    </li>
                  </ul>
                </li>
                <li>Click "Send Invite"</li>
                <li>
                  I'll receive an email notification and accept the invitation within 24 hours.
                </li>
              </ol>
              <div className="p-3 mt-4 border-l-4 border-primary-500 bg-primary-50">
                <p className="text-sm text-neutral-700">
                  <strong>Additional Resource:</strong>{" "}
                  <a
                    href="https://artists.amazonmusic.com/faqs#faq-question-how-do-i-add-another-team-member-to-view-my-artists-stats"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-700 hover:text-primary-700 hover:underline"
                  >
                    Amazon Music FAQ - Adding Team Members
                    <ExternalLink size={14} className="inline ml-1" />
                  </a>
                </p>
              </div>
            </div>
          ),
        };

      default:
        return {
          title: "Add Team Member",
          instructions: (
            <div className="text-sm text-neutral-700">
              <p>Instructions for adding team members are not available for this platform.</p>
            </div>
          ),
        };
    }
  };

  const content = getModalContent();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-800 bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 bg-white rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-black">{content.title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 transition hover:text-neutral-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Instructions Content */}
        <div className="mb-6">{content.instructions}</div>

        {/* Close Button */}
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onClose}
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTeamMemberModal;
